import { useMutation, useQueryClient } from '@tanstack/react-query';
import { houseApi } from '../api/houseApi';
import type { House, CreateHouseData } from '../api/houseApi';
import {
  showApiError,
  createRetryHandler,
} from '../../../shared/utils/apiErrorHandler';
import toast from 'react-hot-toast';

export const useHouseMutations = () => {
  const queryClient = useQueryClient();

  // Create house mutation with retry logic
  const createHouse = useMutation({
    mutationFn: (data: CreateHouseData) => {
      const retryHandler = createRetryHandler(3, 1000);
      return retryHandler(() => houseApi.createHouse(data));
    },
    onSuccess: () => {
      // Invalidate and refetch houses list
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      queryClient.invalidateQueries({ queryKey: ['houses-list'] });
      queryClient.invalidateQueries({ queryKey: ['house-overview'] });
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to create house');
    },
  });

  // Update house mutation with retry logic
  const updateHouse = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateHouseData>;
    }) => {
      const retryHandler = createRetryHandler(3, 1000);
      return retryHandler(() => houseApi.updateHouse(id, data));
    },
    onSuccess: (updatedHouse: House) => {
      // Update the specific house in cache
      queryClient.setQueryData(['houses', updatedHouse.id], updatedHouse);
      // Invalidate houses list to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['houses'] });
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to update house');
    },
  });

  // Delete house mutation
  const deleteHouse = useMutation({
    mutationFn: (id: string) => houseApi.deleteHouse(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['houses', deletedId] });
      // Invalidate houses list
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      queryClient.invalidateQueries({ queryKey: ['houses-list'] });
      queryClient.invalidateQueries({ queryKey: ['house-overview'] });
      toast.success('House deleted successfully!');
    },
    onError: (error: any) => {
      showApiError(error, 'Failed to delete house');
    },
  });

  // Helper function to optimistically update house in cache
  const optimisticUpdate = (id: string, updates: Partial<House>) => {
    queryClient.setQueryData(['houses', id], (oldData: House | undefined) => {
      if (!oldData) return oldData;
      return { ...oldData, ...updates };
    });
  };

  // Helper function to invalidate all house-related queries
  const invalidateHouseQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['houses'] });
    queryClient.invalidateQueries({ queryKey: ['houses-list'] });
    queryClient.invalidateQueries({ queryKey: ['house-overview'] });
  };

  return {
    // Mutations
    createHouse,
    updateHouse,
    deleteHouse,

    // Loading states
    isCreating: createHouse.isPending,
    isUpdating: updateHouse.isPending,
    isDeleting: deleteHouse.isPending,

    // Error states
    createError: createHouse.error,
    updateError: updateHouse.error,
    deleteError: deleteHouse.error,

    // Helper functions
    optimisticUpdate,
    invalidateHouseQueries,

    // Combined states
    isLoading:
      createHouse.isPending ||
      updateHouse.isPending ||
      deleteHouse.isPending,

    hasError: !!(
      createHouse.error ||
      updateHouse.error ||
      deleteHouse.error
    ),
  };
};
